import { type Message, useSDK, useStores } from '@littlebird/sdk';
import {
  formatRoutineScheduleSummary,
  formatRelativeDate,
  parseRoutineSchedule,
} from '@littlebird/ui-utils';
import { type ActionImpl, useKBar } from 'kbar';
import { observer } from 'mobx-react-lite';
import { cloneElement, type ReactNode, useMemo } from 'react';

import { Icons } from '@/assets/icons/icons';
import { XStack, YStack } from '@/components/ui';
import { cn } from '@/lib/utils';
import { formatTimeFromHHmm } from '@/utils/date';

import { splitMatch } from './highlight-match';
import { cutSnippet, hasSubmenu, matchingSnippets } from './preview-model';

import type {
  CommandBarActionImpl,
  CommandBarPreview as PreviewData,
} from './actions/types';

const MAX_SNIPPETS = 2;

function Highlighted({ text, query }: { text: string; query: string }) {
  const split = splitMatch(text, query);
  if (!split) return <>{text}</>;
  return (
    <>
      {split.before}
      <mark className="rounded-sm bg-surface-active px-0.5 text-foreground">
        {split.match}
      </mark>
      {split.after}
    </>
  );
}

function Header({
  label,
  date,
  title,
  subtitle,
}: {
  label?: string;
  date?: string;
  title: string;
  subtitle?: string;
}) {
  return (
    <YStack className="gap-1.5">
      {label && (
        <div className="text-xs text-foreground-muted">
          {date ? `${label} · ${formatRelativeDate(date)}` : label}
        </div>
      )}
      <div className="text-lg font-semibold leading-6 text-foreground">
        {title}
      </div>
      {subtitle && (
        <div className="text-xs text-foreground-muted">{subtitle}</div>
      )}
    </YStack>
  );
}

function SnippetCard({ text, query }: { text: string; query: string }) {
  return (
    <div className="rounded-lg border border-border-subtle bg-surface-subtle px-3.5 py-3 text-[13px] leading-5 text-foreground-muted">
      <Highlighted text={text} query={query} />
    </div>
  );
}

function Snippets({ snippets, query }: { snippets: string[]; query: string }) {
  return (
    <>
      {snippets.map((snippet) => (
        <SnippetCard key={snippet} text={snippet} query={query} />
      ))}
    </>
  );
}

const ChatPreview = observer(function ChatPreview({
  preview,
}: {
  preview: Extract<PreviewData, { kind: 'chat' }>;
}) {
  const { searchStore } = useSDK();
  const { threadId, query, mentionCount } = preview;
  const snippets = useMemo(() => {
    if (mentionCount === 0) return [];
    const found: string[] = [];
    for (const item of searchStore.results ?? []) {
      if (item.type !== 'message' || item.threadId !== threadId) continue;
      found.push(matchingSnippets(item.content ?? '', query, 1)[0] ?? '');
      if (found.length === MAX_SNIPPETS) break;
    }
    return found.filter(Boolean);
  }, [searchStore.results, threadId, query, mentionCount]);

  return (
    <>
      <Header
        label="Chat"
        date={preview.updatedAt}
        title={preview.title}
        subtitle={
          mentionCount > 0
            ? `${mentionCount} ${mentionCount === 1 ? 'mention' : 'mentions'}`
            : undefined
        }
      />
      <Snippets snippets={snippets} query={query} />
    </>
  );
});

function ThreadMessage({
  author,
  text,
  query,
  matched,
}: {
  author: string;
  text: string;
  query: string;
  matched: boolean;
}) {
  return (
    <YStack
      className={cn(
        'gap-1 rounded-lg px-3.5 py-3 text-[13px] leading-5',
        matched
          ? 'border border-border-subtle bg-surface-subtle text-foreground'
          : 'text-foreground-muted'
      )}
    >
      <div className="text-xs font-semibold text-foreground-muted">
        {author}
      </div>
      {matched ? (
        <div className="whitespace-pre-wrap break-words">
          <Highlighted text={cutSnippet(text, query)} query={query} />
        </div>
      ) : (
        <div className="line-clamp-6 whitespace-pre-wrap break-words">
          {text}
        </div>
      )}
    </YStack>
  );
}

const authorOf = (message: Pick<Message, 'role'>) =>
  message.role === 'user' ? 'You' : 'Littlebird';

const MessagePreview = observer(function MessagePreview({
  preview,
}: {
  preview: Extract<PreviewData, { kind: 'message' }>;
}) {
  const { messages } = useStores();
  const { threadId, messageId, query } = preview;
  const loaded = messages.isThreadLoaded(threadId);
  const thread = loaded ? messages.getMessagesByThreadId(threadId) : [];
  const index = thread.findIndex((message) => message.id === messageId);
  const visible =
    index === -1 ? [] : thread.slice(Math.max(0, index - 1), index + 2);

  return (
    <>
      <Header label="Chat" date={preview.updatedAt} title={preview.chatTitle} />
      <YStack className="gap-2">
        {visible.length === 0 ? (
          <ThreadMessage
            author="Message"
            text={preview.content}
            query={query}
            matched
          />
        ) : (
          visible.map((message) => (
            <ThreadMessage
              key={message.id}
              author={authorOf(message)}
              text={message.content}
              query={query}
              matched={message.id === messageId}
            />
          ))
        )}
      </YStack>
    </>
  );
});

function DocumentPreview({
  label,
  title,
  content,
  updatedAt,
  query,
}: {
  label: string;
  title: string;
  content: string;
  updatedAt: string;
  query: string;
}) {
  const snippets = useMemo(
    () => matchingSnippets(content, query, MAX_SNIPPETS),
    [content, query]
  );
  return (
    <>
      <Header label={label} date={updatedAt} title={title} />
      <Snippets snippets={snippets} query={query} />
    </>
  );
}

const RoutinePreview = observer(function RoutinePreview({
  preview,
}: {
  preview: Extract<PreviewData, { kind: 'routine' }>;
}) {
  const { routines, reports } = useStores();
  const { reportTemplateId, query } = preview;
  const routine = routines.getByReferenceId(reportTemplateId);
  const schedule = routine
    ? parseRoutineSchedule(routine.recurrenceRule)
    : null;
  const scheduleText = schedule?.isValid
    ? formatRoutineScheduleSummary(schedule, {
        formatTime: formatTimeFromHHmm,
      })
    : '';
  const lastRun = reports.getReportsByTemplateId(reportTemplateId)[0];
  const snippets = useMemo(
    () =>
      [
        routine?.prompt ? cutSnippet(routine.prompt, query) : '',
        preview.content ? matchingSnippets(preview.content, query, 1)[0] : '',
      ].filter(Boolean),
    [routine?.prompt, preview.content, query]
  );

  return (
    <>
      <Header
        label={scheduleText ? `Routine · ${scheduleText}` : 'Routine'}
        title={preview.title}
        subtitle={
          lastRun
            ? `Last run ${formatRelativeDate(lastRun.scheduledAt)}`
            : undefined
        }
      />
      <Snippets snippets={snippets} query={query} />
    </>
  );
});

function SubmenuPreview({ action }: { action: CommandBarActionImpl }) {
  return (
    <>
      <Header title={action.name} />
      <YStack>
        {action.children.map((child) => {
          const option = child as CommandBarActionImpl;
          return (
            <XStack
              key={option.id}
              className={cn(
                'h-[34px] items-center gap-2.5 text-sm',
                option.dangerous ? 'text-danger-11' : 'text-foreground'
              )}
            >
              <div
                className={cn(
                  'flex w-5 flex-shrink-0 items-center justify-center',
                  option.dangerous ? 'text-danger-11' : 'text-foreground-muted'
                )}
              >
                {option.icon
                  ? // @ts-expect-error no icon on ActionImpl
                    cloneElement(option.icon, { size: 14 })
                  : null}
              </div>
              <span className="min-w-0 flex-1 truncate">{option.name}</span>
              {option.selected && (
                <Icons.check
                  size={14}
                  className="flex-shrink-0 text-foreground-muted"
                />
              )}
            </XStack>
          );
        })}
      </YStack>
      <div className="text-xs text-foreground-muted">→ to choose</div>
    </>
  );
}

function PlainPreview({ action }: { action: ActionImpl }) {
  return (
    <>
      <Header title={action.name} />
      {typeof action.subtitle === 'string' && action.subtitle && (
        <div className="text-[13px] text-foreground-muted">
          {action.subtitle}
        </div>
      )}
    </>
  );
}

function PreviewContent({ action }: { action: CommandBarActionImpl }) {
  const { preview } = action;
  if (!preview) {
    return hasSubmenu(action) ? (
      <SubmenuPreview action={action} />
    ) : (
      <PlainPreview action={action} />
    );
  }
  switch (preview.kind) {
    case 'chat':
      return <ChatPreview preview={preview} />;
    case 'message':
      return <MessagePreview preview={preview} />;
    case 'meeting':
      return (
        <DocumentPreview
          label={preview.label}
          title={preview.title}
          content={preview.content}
          updatedAt={preview.updatedAt}
          query={preview.query}
        />
      );
    case 'journal':
      return (
        <DocumentPreview
          label="Journal"
          title={preview.title}
          content={preview.content}
          updatedAt={preview.updatedAt}
          query={preview.query}
        />
      );
    case 'routine':
      return <RoutinePreview preview={preview} />;
    case 'see_all':
      return (
        <>
          <Header
            title={`${preview.total} ${preview.section.toLowerCase()} mention “${preview.query}”`}
          />
          <div className="text-xs text-foreground-muted">
            {`Shows every result, scoped to ${preview.section}. Tab cycles scopes.`}
          </div>
        </>
      );
  }
}

export const CommandBarPreview = observer(function CommandBarPreview({
  results,
}: {
  results: (string | ActionImpl)[];
}): ReactNode {
  const { activeIndex } = useKBar((state) => ({
    activeIndex: state.activeIndex,
  }));
  const item = results[activeIndex];
  if (!item || typeof item === 'string') return null;

  return (
    <YStack className="h-full gap-4 overflow-y-auto px-6 py-[22px]">
      <PreviewContent key={item.id} action={item as CommandBarActionImpl} />
    </YStack>
  );
});
